import { enumerateElements } from './templateControlNormalization';

import type {
  ControlConfiguration,
  Source,
} from '@src/components/sidepanel/CompositionSettings/types';

/**
 * Prune `source.fonts[]` down to only the font faces the composition actually
 * references, so a saved template carries a clean font list (PLAT-10206).
 *
 * Why this is needed: `CustomFontsService.getCustomFontsSource` injects EVERY
 * non-archived brand variant (plus the default web fonts) into `source.fonts[]`
 * at render time. Without pruning, that bloated list gets persisted on save even
 * though a composition typically uses only one or two faces.
 *
 * "Used" is the union of:
 *   - every element's `font_family` in the source tree (the base faces), and
 *   - every Font control's `customValue` (the current font picks — these
 *     override the base `font_family` on apply but are NOT written back into
 *     `source.elements`, which stays the static base).
 * Taking the union guarantees we never drop a referenced face. Rendering is
 * unaffected either way: getCustomFontsSource re-injects whatever a render
 * needs; this only trims the PERSISTED list.
 *
 * Pure + idempotent: re-running yields the same result, and the input source is
 * returned unchanged when there is nothing to prune.
 */
export const pruneUnusedSourceFonts = (
  source: Source,
  controls?: ControlConfiguration[] | null,
): Source => {
  if (!source || typeof source !== 'object') return source;
  const fonts = (source as { fonts?: unknown }).fonts;
  if (!Array.isArray(fonts) || fonts.length === 0) return source;

  const used = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v === 'string' && v.trim()) used.add(v.trim());
  };

  // (a) Base font_family declared on each element in the tree.
  for (const { element } of enumerateElements(source)) {
    add((element as { font_family?: unknown }).font_family);
  }
  // (b) Current font picks held as Font control customValues (override base).
  if (Array.isArray(controls)) {
    for (const c of controls) {
      if (c?.type === 'font') add(c.customValue);
    }
  }

  const kept = fonts.filter(f =>
    used.has(String((f as { family?: unknown })?.family ?? '').trim()),
  );

  // Nothing pruned → return the original reference so callers/idempotency
  // checks can rely on referential stability.
  if (kept.length === fonts.length) return source;
  return { ...source, fonts: kept };
};
